import * as React from "react"
import { X } from "lucide-react"

import { useBackToClose } from "@/lib/use-back-to-close"
import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogNonModalOverlay,
  DialogPortal,
  DialogTitle,
} from "@/shadcn/components/ui/dialog"

/**
 * The plain modal: a title, a line under it, an optional body, and a footer.
 * Figma: S311lGXE6JSyTnNAXHmtR7, page "dlvr.it / simple modal", frame
 * 30031-3207 ("Activate this output?").
 *
 * 440px card, 24px between blocks, radius 16. The header is CENTRED, with a
 * 28px spacer opposite the close button so the text centres on the card rather
 * than on the space left beside the X. The footer is horizontal: Cancel left,
 * primary right.
 *
 * NOTE FOR REVIEW - the frame disagrees with the modal recipe in the design
 * conventions, which specifies 28px between blocks and a stacked full-width
 * footer with the primary first, and records that the stacked footer REPLACED
 * the horizontal one on the Connect Social and confirmation modals. This is
 * built as drawn. If horizontal is the standard again then FirstSuccessDialog
 * and the Connect Social modals are the inconsistent ones, which is a bigger
 * change than this file and not one to make by inference.
 *
 * Open is not a state: the legacy modal stack mounts this to show it and
 * unmounts it to close it, so mounted IS open - the same setup
 * FirstSuccessDialog and ConnectSocialDialog use, and for the same reason
 * (Radix's `modal` would put `pointer-events: none` on <body> and kill any
 * legacy modal raised over it).
 */

/** 44px on a phone (the iOS HIG minimum), the primitive's 32 from `md`. */
const TAP_TARGET = "tw:h-11 tw:md:h-8"

type SimpleModalProps = {
  title: string
  /** The line under the title. Omit for a modal whose body says everything. */
  description?: React.ReactNode
  /** Anything between the header and the footer: a field, a list, a preview. */
  children?: React.ReactNode
  /** Swaps the confirm button's variant and nothing else. */
  tone?: "default" | "destructive"
  confirmLabel: string
  /** `null` hides it - for a modal that is acknowledged rather than decided. */
  cancelLabel?: string | null
  /** In flight: the confirm spins, cancel locks. */
  busy?: boolean
  busyLabel?: string
  showClose?: boolean
  onConfirm: () => void
  /** Defaults to `onClose`, since cancelling a plain modal is closing it. */
  onCancel?: () => void
  onClose: () => void
  className?: string
}

export default function SimpleModal({
  title,
  description,
  children,
  tone = "default",
  confirmLabel,
  cancelLabel = "Cancel",
  busy = false,
  busyLabel = "Working...",
  showClose = true,
  onConfirm,
  onCancel,
  onClose,
  className,
}: SimpleModalProps) {
  const destructive = tone === "destructive"

  // The scroll lock Radix would have given us, declined below with modal={false}.
  React.useEffect(() => {
    document.body.classList.add("modal-open")

    return () => {
      document.body.classList.remove("modal-open")
    }
  }, [])

  // Back dismisses this, as it does every legacy modal.
  useBackToClose(onClose)

  return (
    <Dialog
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      {/* Radix renders no overlay for a non-modal dialog; this puts the scrim
          back and is also what closes the card on a click beside it. */}
      <DialogPortal>
        <DialogNonModalOverlay onClick={onClose} />
      </DialogPortal>
      <DialogContent
        showCloseButton={false}
        /* The scrim above owns outside-dismissal. Radix's own detection misfires
           through this portal with modal={false} — see ConnectSocialDialog. */
        onInteractOutside={(event) => {
          event.preventDefault()
        }}
        /* DialogPortal already wraps its children in `.tw-root`, so there is no
           portal patch to add here. Padding is p-4 below `md` rather than the
           frame's flat 24, which gives a phone 16px back; desktop is the frame. */
        className={cn(
          "tw:flex tw:flex-col tw:gap-6 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-4 tw:text-card-foreground tw:shadow-xs tw:ring-0 tw:sm:max-w-[440px] tw:md:p-6",
          className
        )}
      >
        {/*
         * As the frame draws it: the text runs the card's full width and the
         * close button sits over the title row's right end. Only the title
         * steps in, 36px each side (the 28px button and 8 clear of it), so it
         * stays centred on the card and never runs under the X; the
         * description below starts under the button and gets the whole width,
         * which on a phone is most of what keeps a modal short.
         */}
        <div className="tw:relative tw:flex tw:w-full tw:flex-col tw:gap-2 tw:text-center">
          <DialogTitle
            className={cn(
              "tw:text-lg tw:leading-7 tw:font-semibold tw:text-foreground",
              showClose && "tw:px-9"
            )}
          >
            {title}
          </DialogTitle>
          {/* Radix logs a dev warning for a dialog with no description, so
              give every modal one; the frame does. */}
          {description ? (
            <DialogDescription className="tw:leading-5">
              {description}
            </DialogDescription>
          ) : null}

          {showClose ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close"
              onClick={onClose}
              className="tw:absolute tw:top-0 tw:right-0 tw:text-muted-foreground tw:hover:text-foreground"
            >
              <X className="tw:size-4" />
            </Button>
          ) : null}
        </div>

        {children ? (
          <div className="tw:flex tw:w-full tw:min-w-0 tw:flex-col tw:gap-4">
            {children}
          </div>
        ) : null}

        {/*
         * The frame's horizontal footer. Below `sm` it stacks and the buttons
         * fill, because two hug buttons at opposite ends of a 343px card leave
         * a hole in the middle and two small targets at the edges.
         *
         * `flex-col-reverse` for that stack, so the DOM keeps the frame's order
         * (Cancel first) while the phone shows the primary on top.
         */}
        <div
          className={cn(
            "tw:flex tw:w-full tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:items-center",
            cancelLabel === null ? "tw:sm:justify-end" : "tw:sm:justify-between"
          )}
        >
          {cancelLabel === null ? null : (
            <Button
              variant="outline"
              className={TAP_TARGET}
              disabled={busy}
              onClick={onCancel ?? onClose}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={destructive ? "destructive" : "default"}
            className={TAP_TARGET}
            loading={busy}
            onClick={onConfirm}
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
